// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './ClaimCta.module.css'
import { ownerLinkMessage } from './OwnerLinkMessage'
import { ownerApi, OwnerApiError } from 'lib/owner-notes/api'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import { useOwnerAuth, type OwnerAuth, type OwnerCredential } from 'lib/owner-notes/auth-bridge'
import { maskEmail } from 'lib/owner-notes/link'
import { SIGN_IN_PUBLIC_PAGE_LINE } from 'lib/owner-notes/band-copy'
import {
  GOOGLE_REQUIRED_CODE,
  GOOGLE_REQUIRED_MESSAGE,
  OWNER_HELD_REVIEW_MESSAGE,
  type OwnerClaimResponse,
} from 'lib/dealhunt/types'

interface ClaimCtaProps {
  trackOwner: OwnerTrack
  /** Where the CTA sits — `band` / `own_note` / `section` / `profile` (analytics only). */
  location: string
  /** The line above the buttons; defaults to DealHunt's held-review sentence (#64). Callers that
   *  sit on a held note render this CTA only when `heldClaimOffered` says signing in publishes it
   *  (#121) — never pass a moderator-only note here. */
  lead?: string
  onClaimed: (result: OwnerClaimResponse) => void
  className?: string
  /**
   * The signed-in reader's masked email (#81). With a session the click links without any
   * widget, so the button reads "Link my account (g•••@gmail.com)". Omitted: read from the
   * session — a signed-in reader's click links without a widget wherever the CTA sits.
   */
  signedInEmail?: string | null
  /** The line under the button; defaults to the pseudonym-and-email sentence. */
  fine?: string
  /** `inline`: the lead and a text button in one line (the category tile), no fine print. */
  variant?: 'block' | 'inline'
  /** The button's words, signed out / signed in. */
  labels?: { signIn: string; link: string }
  /** What the button does after a failed pre-publish link: default `link`. */
  retry?: 'link' | 'sign_in' | 'none'
  /** `block` only: what the sign-in adds, between the lead and the button (the accept result's
   *  "Make it count" list). */
  children?: React.ReactNode
}

/**
 * "Keep @handle / publish now" — the claim CTA (contract §3.3, #34, #64, #67). Opens the
 * live SoundGuys sign-in (`useOwnerAuth` — Google or an email link/code → commentHub), then
 * hands the session's credential to `POST /api/owner-notes/claim`, which verifies it and links
 * the identity upstream. One provider-neutral "Sign in" button: the dialog offers both methods
 * (no separate "Use an email link instead" button, 2026-09-11).
 *
 * Arms on click only: a `session-updated` from the page's own session check never claims
 * on the reader's behalf.
 */
const ClaimCta: React.VFC<ClaimCtaProps> = ({
  trackOwner,
  location,
  lead,
  onClaimed,
  className,
  signedInEmail,
  fine = `Your pseudonym stays; your email is only used to keep your reviews together across devices and to tell you when a review is published. ${SIGN_IN_PUBLIC_PAGE_LINE}`,
  variant = 'block',
  labels,
  retry = 'link',
  children,
}) => {
  const { credential, provider, showLogIn, signedIn, email } = useOwnerAuth()
  const sessionEmail = signedIn ? maskEmail(email) : null
  const linkEmail = signedInEmail === undefined ? sessionEmail : signedInEmail
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const claimingRef = useRef(false)
  /** The token present when the widget was armed: a re-sign-in must yield a NEW one. */
  const armedTokenRef = useRef<string | null>(null)

  const claim = async (current: OwnerCredential, label: OwnerAuth['provider']): Promise<void> => {
    if (claimingRef.current) {
      return
    }
    claimingRef.current = true
    setBusy(true)
    setError(null)
    try {
      const result = await ownerApi.claim(current)
      trackOwner('claim_done', {
        action: label ?? 'other',
        item_count: result.published_now,
      })
      onClaimed(result)
    } catch (e) {
      if (e instanceof OwnerApiError && e.code === GOOGLE_REQUIRED_CODE) {
        // The joinable-identity gate (D5): the fixed sentence, rendered with its settings link.
        setError(GOOGLE_REQUIRED_MESSAGE)
      } else {
        setError(e instanceof OwnerApiError ? e.message : 'Sign-in linking failed — try again.')
      }
    } finally {
      claimingRef.current = false
      setBusy(false)
      setArmed(false)
    }
  }

  // The sign-in widget resolves when DISPLAYED, not when signed in — the session lands here.
  useEffect(() => {
    if (armed && credential && credential.token !== armedTokenRef.current) {
      void claim(credential, provider)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- claim is stable enough per arm
  }, [armed, credential?.token])

  const start = async (): Promise<void> => {
    trackOwner('claim_started', { location })
    if (credential && retry !== 'sign_in') {
      await claim(credential, provider)
      return
    }
    armedTokenRef.current = credential?.token ?? null
    setArmed(true)
    await showLogIn()
  }

  // With a session the click links without any widget (#81) — say so on the button.
  const oneClick = Boolean(linkEmail) && retry === 'link'
  const linkLabel = labels?.link ?? `Link my account (${linkEmail})`
  const primaryLabel = oneClick ? linkLabel : labels?.signIn ?? 'Sign in'

  if (variant === 'inline') {
    return (
      <p className={clsx(styles.inline, className)}>
        {lead && <span>{ownerLinkMessage(lead)} </span>}
        <button
          type="button"
          className={styles.inlineButton}
          disabled={busy}
          onClick={() => void start()}
        >
          {busy ? 'Linking…' : primaryLabel}
        </button>
        {error && <span className={styles.error}> {ownerLinkMessage(error)}</span>}
      </p>
    )
  }

  return (
    <div className={clsx(styles.root, className)}>
      <p className={styles.lead}>{ownerLinkMessage(lead ?? OWNER_HELD_REVIEW_MESSAGE)}</p>
      {children}
      {retry !== 'none' && (
        <div className={styles.buttons}>
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => void start()}
          >
            {busy ? 'Linking…' : primaryLabel}
          </button>
        </div>
      )}
      <p className={styles.fine}>{fine}</p>
      {error && <p className={styles.error}>{ownerLinkMessage(error)}</p>}
    </div>
  )
}

export default ClaimCta
